import React, { useState, useCallback, useEffect } from 'react';
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { useDropzone } from 'react-dropzone';
import {
  Upload,
  FileText,
  FileSpreadsheet,
  Presentation,
  FileType,
  Download,
  Loader2,
  CheckCircle,
  XCircle,
  ArrowRight,
  RefreshCw,
  Trash2,
  Eye,
  Clock,
  Sparkles,
  File
} from "lucide-react";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "");

// Iconos por tipo de formato
const formatIcons = {
  pdf: <FileText className="w-5 h-5 text-red-600" />,
  doc: <FileText className="w-5 h-5 text-blue-600" />,
  docx: <FileText className="w-5 h-5 text-blue-600" />,
  xls: <FileSpreadsheet className="w-5 h-5 text-green-600" />,
  xlsx: <FileSpreadsheet className="w-5 h-5 text-green-600" />,
  ppt: <Presentation className="w-5 h-5 text-orange-600" />,
  pptx: <Presentation className="w-5 h-5 text-orange-600" />,
  txt: <File className="w-5 h-5 text-gray-600" />,
  html: <FileType className="w-5 h-5 text-purple-600" />
};

const ConverterPage = () => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [targetFormat, setTargetFormat] = useState('');
  const [useAI, setUseAI] = useState(true);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [supportedFormats, setSupportedFormats] = useState({ input_formats: [], output_formats: [] });

  // Cargar formatos soportados
  useEffect(() => {
    const fetchFormats = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/converter/formats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setSupportedFormats(data);
        }
      } catch (error) {
        console.error('Error loading formats:', error);
      }
    };
    fetchFormats();
    fetchHistory();
  }, []);

  // Cargar historial
  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/converter/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Manejo de drop de archivos
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    setUploading(true);
    setUploadedFile(null);
    setTargetFormat('');
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/api/converter/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Error al cargar archivo');
      }
      
      const data = await res.json();
      setUploadedFile(data);
      toast.success('Archivo cargado exitosamente');
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'text/plain': ['.txt'],
      'application/rtf': ['.rtf']
    },
    maxFiles: 1
  });

  // Convertir documento
  const handleConvert = async () => {
    if (!uploadedFile || !targetFormat) {
      toast.error('Seleccione un formato de salida');
      return;
    }
    
    setConverting(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('target_format', targetFormat);
      formData.append('use_ai', useAI.toString());
      
      const res = await fetch(`${API_URL}/api/converter/convert/${uploadedFile.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Error en la conversión');
      }
      
      // Descargar el archivo convertido
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${uploadedFile.filename.split('.')[0]}_converted.${targetFormat}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('¡Conversión completada!');
      fetchHistory();
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setConverting(false);
    }
  };

  // Descargar archivo del historial
  const handleDownload = async (conversionId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/converter/download/${conversionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Error descargando archivo');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Eliminar del historial
  const handleDelete = async (conversionId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/converter/history/${conversionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Conversión eliminada');
        fetchHistory();
      }
    } catch (error) {
      toast.error('Error eliminando conversión');
    }
  };

  // Formatear tamaño de archivo
  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Obtener ícono del formato
  const getFormatIcon = (format) => {
    const ext = format?.replace('.', '').toLowerCase();
    return formatIcons[ext] || <File className="w-5 h-5 text-gray-500" />;
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-6 pt-14 md:pt-6 lg:p-8 overflow-auto" data-testid="converter-page">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-3">
              <RefreshCw className="w-8 h-8 text-blue-600" />
              Convertidor de Documentos
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-2">
              Convierte documentos entre diferentes formatos con precisión usando IA
            </p>
          </div>

          <Tabs defaultValue="convert" className="space-y-6">
            <TabsList>
              <TabsTrigger value="convert" className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                Nueva Conversión
              </TabsTrigger>
              <TabsTrigger value="history" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Historial ({history.length})
              </TabsTrigger>
            </TabsList>

            {/* Tab: Nueva Conversión */}
            <TabsContent value="convert" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Upload Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Upload className="w-5 h-5 text-blue-600" />
                      Cargar Documento
                    </CardTitle>
                    <CardDescription>
                      Sube el archivo que deseas convertir
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div
                      {...getRootProps()}
                      className={`
                        border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
                        ${isDragActive 
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                          : 'border-slate-300 dark:border-slate-600 hover:border-blue-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                        }
                        ${uploading ? 'pointer-events-none opacity-50' : ''}
                      `}
                    >
                      <input {...getInputProps()} data-testid="file-input-converter" />
                      {uploading ? (
                        <div className="flex flex-col items-center">
                          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
                          <p className="text-slate-600 dark:text-slate-400">Procesando archivo...</p>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                          <p className="text-slate-600 dark:text-slate-400 mb-2">
                            {isDragActive ? 'Suelta el archivo aquí' : 'Arrastra un archivo o haz clic para seleccionar'}
                          </p>
                          <p className="text-xs text-slate-500">
                            Formatos: PDF, Word, Excel, PowerPoint, TXT
                          </p>
                        </>
                      )}
                    </div>

                    {/* Archivo cargado */}
                    {uploadedFile && (
                      <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                        <div className="flex items-center gap-3 mb-3">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-green-800 dark:text-green-300">Archivo cargado</span>
                        </div>
                        <div className="flex items-center gap-3">
                          {getFormatIcon(uploadedFile.format)}
                          <div className="flex-1">
                            <p className="font-medium text-slate-800 dark:text-white">{uploadedFile.filename}</p>
                            <p className="text-sm text-slate-500">{formatFileSize(uploadedFile.size)}</p>
                          </div>
                          <Badge variant="outline">{uploadedFile.format?.toUpperCase()}</Badge>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Conversion Options */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ArrowRight className="w-5 h-5 text-green-600" />
                      Opciones de Conversión
                    </CardTitle>
                    <CardDescription>
                      Selecciona el formato de salida deseado
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Formato de salida */}
                    <div className="space-y-2">
                      <Label>Convertir a:</Label>
                      <Select
                        value={targetFormat}
                        onValueChange={setTargetFormat}
                        disabled={!uploadedFile}
                      >
                        <SelectTrigger data-testid="target-format-select">
                          <SelectValue placeholder="Seleccionar formato de salida" />
                        </SelectTrigger>
                        <SelectContent>
                          {uploadedFile?.available_outputs?.map((format) => (
                            <SelectItem key={format} value={format}>
                              <div className="flex items-center gap-2">
                                {getFormatIcon(format)}
                                <span>{format.toUpperCase()}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Opción de IA */}
                    <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Sparkles className="w-5 h-5 text-purple-600" />
                        <div>
                          <p className="font-medium text-slate-800 dark:text-white">Mejora con IA</p>
                          <p className="text-xs text-slate-500">Optimiza la estructura y formato del documento</p>
                        </div>
                      </div>
                      <Switch
                        checked={useAI}
                        onCheckedChange={setUseAI}
                        disabled={!uploadedFile}
                      />
                    </div>

                    {/* Botón de conversión */}
                    <Button
                      onClick={handleConvert}
                      disabled={!uploadedFile || !targetFormat || converting}
                      className="w-full h-12 text-lg"
                      data-testid="convert-btn"
                    >
                      {converting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Convirtiendo...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-5 h-5 mr-2" />
                          Convertir Documento
                        </>
                      )}
                    </Button>

                    {/* Preview del contenido */}
                    {uploadedFile?.extracted_preview && (
                      <div className="mt-4">
                        <Label className="flex items-center gap-2 mb-2">
                          <Eye className="w-4 h-4" />
                          Vista previa del contenido:
                        </Label>
                        <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg max-h-48 overflow-y-auto text-sm text-slate-600 dark:text-slate-400 font-mono">
                          {uploadedFile.extracted_preview}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Formatos soportados */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Formatos Soportados</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {supportedFormats.input_formats?.map((format) => (
                      <div 
                        key={format.extension} 
                        className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                      >
                        {getFormatIcon(format.extension)}
                        <span className="text-sm font-medium">{format.name}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Historial */}
            <TabsContent value="history">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-slate-600" />
                      Historial de Conversiones
                    </span>
                    <Button variant="outline" size="sm" onClick={fetchHistory}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${loadingHistory ? 'animate-spin' : ''}`} />
                      Actualizar
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {history.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No hay conversiones en el historial</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Archivo Original</TableHead>
                          <TableHead>Conversión</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead>Fecha</TableHead>
                          <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {history.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getFormatIcon(item.original_format)}
                                <span className="truncate max-w-[200px]">{item.original_filename}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">{item.original_format?.replace('.', '').toUpperCase()}</Badge>
                                <ArrowRight className="w-4 h-4 text-slate-400" />
                                <Badge>{item.target_format?.toUpperCase() || 'N/A'}</Badge>
                              </div>
                            </TableCell>
                            <TableCell>
                              {item.status === 'completed' ? (
                                <Badge className="bg-green-100 text-green-800">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Completado
                                </Badge>
                              ) : item.status === 'failed' ? (
                                <Badge variant="destructive">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Error
                                </Badge>
                              ) : (
                                <Badge variant="secondary">
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  {item.status}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-slate-500 text-sm">
                              {new Date(item.created_at).toLocaleDateString('es-DO', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                {item.status === 'completed' && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDownload(item.id, item.output_filename || `converted.${item.target_format}`)}
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(item.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default ConverterPage;
